import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex divide-x divide-gray-200 -mx-4" }
const _hoisted_2 = { class: "w-3/12 px-4 flex flex-col justify-between" }
const _hoisted_3 = { class: "space-y-2 text-sm" }
const _hoisted_4 = { class: "flex" }
const _hoisted_5 = { class: "leading-none text-gray-900 font-medium" }
const _hoisted_6 = { class: "leading-none text-gray-500" }
const _hoisted_7 = {
  key: 0,
  class: "leading-none"
}
const _hoisted_8 = { class: "-m-1.5" }
const _hoisted_9 = { class: "table-auto w-full" }
const _hoisted_10 = { class: "text-gray-500 text-sm font-normal mt-2" }
const _hoisted_11 = { class: "w-4/12 px-4 leading-none" }
const _hoisted_12 = { class: "table-auto w-full" }
const _hoisted_13 = { class: "w-5/12 px-4 space-y-3" }

import { computed, PropType, ref } from 'vue';
import { TranslateResult, useI18n } from 'vue-i18n';
import { BaseCard } from '@/lib/components/Card';
import {
  changeHomeMonitoringPlanReasons,
  dischargeReasons,
  dischargeReasonsCopdAa,
  measurementReadingsList,
  reviewOutcomes
} from '@/helpers/home-monitoring.helper';
import CopdMeasurementRow from './CopdMeasurementRow.vue';
import CopdUhbModifiedAnthonisenCriteriaMeasurementReading
  from './CopdUhbModifiedAnthonisenCriteriaMeasurementReading.vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import { CopdOutcome, CopdProgramMeasurement, CopdProgramReview, CopdProgramSettingResource, CopdReason, Worklist } from '@/models';
import { ITranslatedOption } from '@/lib';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { FEATURES } from '@/constants';
import { HomeMonitoringPathways } from '@/lib/components/Programs/IbdProgram/constants';
import { BadgeKind, MedKitBadge } from '@bigpicturemedical/medkit';

enum ReviewedStates {
  Reviewed = 'reviewed',
  UnreviewedHigh = 'unreviewed-high',
  Unreviewed = 'unreviewed'
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CopdMeasurement',
  props: {
  measurement: {
    type: Object as PropType<CopdProgramMeasurement>,
    required: true
  },
  review: {
    type: Object as PropType<CopdProgramReview | null>,
    default: null
  },
  settings: {
    type: Object as PropType<CopdProgramSettingResource>,
    required: true
  },
  worklist: {
    type: Object as PropType<Worklist>,
    default: () => null
  }
},
  setup(__props) {

dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(utc);

const { t } = useI18n();
const props = __props;

const isCopdAdmissionAvoidanceFeatureEnabled = ref<boolean>(isFeatureFlagEnabled(FEATURES.COPD_ADMISSION_AVOIDANCE));
const isReviewed = computed<boolean>(() => !!props.measurement.relationships.review);
const isChange = computed<boolean>(() => props.review?.attributes.outcome === CopdOutcome.change);
const isDischarge = computed<boolean>(() => props.review?.attributes.outcome === CopdOutcome.discharge);
const createdAt = computed(() => {
  const date = formatDate(props.measurement.attributes.created_at);

  const time = dayjs.utc(props.measurement.attributes.created_at).local().format('HH:mm');

  return t('custom.uhb.copd.created-at', [date, time]);
});
const reviewedAt = computed<TranslateResult|undefined>(() => {
  if (props.review === null) {
    return t('custom.uhb.copd.reviewed-at-unknown');
  }

  return props.review && formatDate(props.review?.attributes.created_at);
});
const labelState = computed<ReviewedStates>(() => {
  if (isReviewed.value) {
    return ReviewedStates.Reviewed;
  }

  if (props.measurement.attributes.high_risk) {
    return ReviewedStates.UnreviewedHigh;
  }

  return ReviewedStates.Unreviewed;
});
const labelColor = computed<string>(() => ({
  [ReviewedStates.Reviewed]: 'stable',
  [ReviewedStates.Unreviewed]: 'low',
  [ReviewedStates.UnreviewedHigh]: 'critical'
}[labelState.value]));
const uhbModifiedAnthonisenCriteriaMeasurementReadings = computed<Array<ITranslatedOption>>(() =>
  measurementReadingsList(props.measurement.attributes.uhb_modified_anthonisen_criteria)
);
const formatDate = (value: string): TranslateResult | string => {
  const date = dayjs.utc(value).local();

  if (date.isToday()) {
    return t('platform.common.today');
  }

  if (date.isYesterday()) {
    return t('platform.common.yesterday');
  }

  return date.format('D MMM YYYY');
};
const reasonLabel = (key: CopdReason): string => {
  if (isChange.value) {
    return changeHomeMonitoringPlanReasons.find(({ value }) => (value as CopdReason) === key)?.label.toString() || '';
  }

  if (isDischarge.value) {
    let reasons = dischargeReasons;
    if (isCopdAdmissionAvoidanceFeatureEnabled.value && props.worklist?.slug === HomeMonitoringPathways.CopdAdmissionAvoidance) {
      reasons = dischargeReasonsCopdAa;
    }
    return reasons.find(({ value }) => (value as CopdReason) === key)?.label.toString() || '';
  }

  return '';
};
const outcomeLabel = (key: string): string => {
  const outcome = reviewOutcomes.find(({ value }) => value === key);
  return outcome ? outcome.label.toString() : '';
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BaseCard), { padding: "5" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(MedKitBadge), {
                label: _ctx.$t('custom.uhb.copd.' + labelState.value, [reviewedAt.value]),
                kind: labelColor.value as BadgeKind,
                size: "small",
                tint: !isReviewed.value
              }, null, 8, ["label", "kind", "tint"])
            ]),
            _createElementVNode("div", _hoisted_5, _toDisplayString(createdAt.value), 1),
            _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t('custom.uhb.copd.created-by', [__props.measurement.attributes.created_by])), 1)
          ]),
          (isReviewed.value && __props.review)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("table", _hoisted_9, [
                    _createVNode(CopdMeasurementRow, {
                      label: _ctx.$t('custom.uhb.copd.outcome'),
                      value: outcomeLabel(__props.review.attributes.outcome)
                    }, null, 8, ["label", "value"]),
                    (isChange.value || isDischarge.value)
                      ? (_openBlock(), _createBlock(CopdMeasurementRow, {
                          key: 0,
                          label: _ctx.$t('custom.uhb.copd.outcome-reason'),
                          value: reasonLabel(__props.review.attributes.reason)
                        }, null, 8, ["label", "value"]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t('custom.uhb.copd.reviewed-by')) + " " + _toDisplayString(__props.review.attributes.created_by), 1)
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("table", _hoisted_12, [
            _createVNode(CopdMeasurementRow, {
              label: _ctx.$t('custom.uhb.copd.spo2'),
              value: __props.measurement.attributes.pulse_oximetry.spo2
              ? _ctx.$t('custom.uhb.copd.spo2-value', [__props.measurement.attributes.pulse_oximetry.spo2])
              : '—',
              target: _ctx.$t('custom.uhb.copd.spo2-prescribed-value', [__props.settings.spo2_range_low, __props.settings.spo2_range_high]),
              alert: __props.measurement.attributes.pulse_oximetry.spo2
              && __props.measurement.attributes.pulse_oximetry.spo2 <= __props.settings.spo2_range_low
            }, null, 8, ["label", "value", "target", "alert"]),
            _createVNode(CopdMeasurementRow, {
              label: _ctx.$t('custom.uhb.copd.inspired-oxygen'),
              value: __props.measurement.attributes.pulse_oximetry.method_of_oxygen_delivery.label,
              target: `(${__props.settings.method_of_oxygen_delivery.label})`
            }, null, 8, ["label", "value", "target"]),
            _createVNode(CopdMeasurementRow, {
              label: _ctx.$t('custom.uhb.copd.flow-rate'),
              value: 
              __props.measurement.attributes.pulse_oximetry.flow_rate
                ? `${__props.measurement.attributes.pulse_oximetry.flow_rate} ${_ctx.$t('custom.uhb.copd.litres-per-min')}`
                : `—`
            ,
              target: __props.settings.flow_rate ? `(${__props.settings.flow_rate} ${_ctx.$t('custom.uhb.copd.litres-per-min')})` : `—`
            }, null, 8, ["label", "value", "target"]),
            _createVNode(CopdMeasurementRow, {
              label: _ctx.$t('custom.uhb.copd.borg'),
              value: __props.measurement.attributes.borg_breathlessness_scale.amount_of_breathlessness.value
            }, null, 8, ["label", "value"]),
            _createVNode(CopdMeasurementRow, {
              label: _ctx.$t('custom.uhb.copd.heart-rate'),
              value: 
              __props.measurement.attributes.pulse.rate
                ? _ctx.$t('custom.uhb.copd.heart-rate-value', [__props.measurement.attributes.pulse.rate])
                : `—`
            
            }, null, 8, ["label", "value"]),
            (__props.measurement.attributes.respiration)
              ? (_openBlock(), _createBlock(CopdMeasurementRow, {
                  key: 0,
                  label: _ctx.$t('custom.uhb.copd.respiration-rate'),
                  value: 
              __props.measurement.attributes.respiration.rate
                ? _ctx.$t('custom.uhb.copd.respiration-rate-value', [__props.measurement.attributes.respiration.rate])
                : '—'
            
                }, null, 8, ["label", "value"]))
              : _createCommentVNode("", true),
            (__props.measurement.attributes.body_temperature)
              ? (_openBlock(), _createBlock(CopdMeasurementRow, {
                  key: 1,
                  label: _ctx.$t('custom.uhb.copd.temperature'),
                  value: 
              _ctx.$t('custom.uhb.copd.temperature-value', [__props.measurement.attributes.body_temperature.temperature.magnitude])
            
                }, null, 8, ["label", "value"]))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_13, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(uhbModifiedAnthonisenCriteriaMeasurementReadings.value, (uhbModifiedAnthonisenCriteriaMeasurementReading) => {
            return (_openBlock(), _createBlock(CopdUhbModifiedAnthonisenCriteriaMeasurementReading, {
              key: uhbModifiedAnthonisenCriteriaMeasurementReading.label,
              label: uhbModifiedAnthonisenCriteriaMeasurementReading.label,
              value: uhbModifiedAnthonisenCriteriaMeasurementReading.value,
              alert: uhbModifiedAnthonisenCriteriaMeasurementReading.alert
            }, null, 8, ["label", "value", "alert"]))
          }), 128))
        ])
      ])
    ]),
    _: 1
  }))
}
}

})